import React, { useState } from 'react';
import ButtonComponent from '../components/ui/ButtonComponent';
import { imagePath, videoPath } from '../utils/imagePath';
import {
  DocumentationCard,
  SuperChargeCard,
  ToolsCard,
} from '../components/ui/UtilsLayout';
import FeatureTabs, { CarouselControls } from '../utils/FeatureTab';
import SignupModal from '../components/SignupModal';
import VideoShimmer from '../components/ShimmerEffects/VideoShimmer';
import { SpatialGridSuperchargeData, superchargeData, SuperChargeVideos, ToolsDetails } from '../components/data/data/PlatformOverview/PlatformOverviewData';

const PlatformOverview = () => {
  const [activeSuperchargeIndex, setActiveSuperchargeIndex] = useState(0);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [isHeroVideoLoaded, setIsHeroVideoLoaded] = useState(false);


  const openSignupModal = () => {
    setShowSignupModal(true);
  };

  return (
    <div className="w-full flex flex-col items-center text-white">
      <div className="w-full max-w-[1460px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 pt-28 sm:pt-28 md:pt-32 lg:pt-40 pb-12 md:pb-16">
        <div className="flex flex-col-reverse lg:flex-row gap-6 md:gap-8 lg:gap-28 w-full">
          <div className="flex-[1] flex flex-col gap-8 sm:gap-10 md:gap-8 lg:gap-6 xl:gap-10 w-full lg:justify-center">
            <div className="text-xl sm:text-2xl md:text-xl lg:text-xl xl:text-3xl text-[#D8D8D8] font-jakarta w-full font-bold leading-5">
              The No-Code Engine Powering Scalable Immersive XR
            </div>

            <div className="text-[#717173] text-base sm:text-lg md:text-[16px] lg:[18px] xl:text-[20px] font-light">
              The Spatial Grid Design Editor simplifies the creation of
              immersive XR experiences for smartphones, VR headsets, & MR
              glasses—offering a seamless no-code approach with the flexibility
              to integrate custom code when needed.
            </div>

            <ButtonComponent
              className="w-full sm:w-auto sm:min-w-[200px] md:max-w-[200px] py-4 !md:py-5 bg-[#EE2B2A] hover:bg-[#EE2B2A] h-auto flex items-center justify-center text-center"
              onClick={openSignupModal}
            >
              Get Started
            </ButtonComponent>
          </div>

          <div className="flex-[2] w-full relative">
            {!isHeroVideoLoaded && <VideoShimmer />}
            <video
              muted
              autoPlay
              playsInline
              src={videoPath?.WebsiteDemo}
              onLoadedData={() => setIsHeroVideoLoaded(true)}
              className={`w-full transition-opacity duration-500 rounded ${
                isHeroVideoLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'
              }`}
            />
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1300px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 flex flex-col gap-10 md:gap-24">
        {/* ================= TOOLS SECTION ================= */}
        <section className="w-full flex flex-col items-center gap-6">
          <DocumentationHeading
            title="Tools to unleash your creativity"
            desc="3D opens a new door for creativity. A solid set of features that will help you create your best 3d designs."
          />

          <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-6 lg:gap-10">
            {ToolsDetails?.map((v, i) => (
              <ToolsCard key={i} img={v.img} desc={v.desc} title={v.title} />
            ))}
          </div>
        </section>

        {/* HEADING FIRST */}
        <DocumentationHeading
          title="How Spatial Grid supercharges your enterprise projects"
          desc="3D opens a new door for creativity. A solid set of features that will help you create your best 3D designs."
        />
        {/* ================= SUPERCHARGE VIDEO + TABS ================= */}
        <section className="w-full flex flex-col gap-5 md:gap-2">
          <div className="flex md:hidden mb-3 justify-end">
            <CarouselControls
              activeIndex={activeSuperchargeIndex}
              total={superchargeData.length}
              onChange={setActiveSuperchargeIndex}
            />
          </div>

          {/* VIDEO SECOND */}
          <div className="max-w-[1200px] w-full mx-auto flex items-center justify-center aspect-[16/9] bg-[#1A1C20] rounded-[10px] border border-gray-800 overflow-hidden">
            {!isHeroVideoLoaded && <VideoShimmer />}
            {superchargeData[activeSuperchargeIndex]?.video ? (
              <video
                key={superchargeData[activeSuperchargeIndex]?.video}
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              >
                <source
                  src={superchargeData[activeSuperchargeIndex]?.video}
                  type="video/mp4"
                />
              </video>
            ) : (
              <div className="text-gray-500">No video available</div>
            )}
          </div>

          {/* TABS LAST */}
          <FeatureTabs
            features={superchargeData}
            defaultActive={0}
            gridCols="md:grid-cols-3"
            activeIndex={activeSuperchargeIndex}
            onTabChange={setActiveSuperchargeIndex}
          />
        </section>

        {/* ================= DOCUMENTATION CARDS ================= */}
        <section className="w-full flex flex-col gap-20 lg:gap-28">
          {SuperChargeVideos.map((v, i) => (
            <DocumentationCard
              key={i}
              index={i}
              title={v.title}
              desc={v.desc}
              videoSrc={v.videoSrc}
              smallImage={v.smallImage}
            />
          ))}
        </section>

        {/* ================= REPEATED TOOLS ================= */}
        <section className="w-full flex flex-col items-center gap-6">
          <DocumentationHeading
            title="How Spatial Grid supercharges your enterprise projects"
            desc="3D opens a new door for creativity. A solid set of features that will help you create your best 3D designs."
          />

          <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-10 md:gap-6 lg:gap-10">
            {SpatialGridSuperchargeData?.map((v, i) => (
              <SuperChargeCard
                key={i}
                img={v.img}
                desc={v.desc}
                title={v.title}
              />
            ))}
          </div>
        </section>

        {/* ================= FINAL SECTION ================= */}
        <div className="w-full flex flex-col gap-4 items-center mb-10 md:mb-20">
          <DocumentationHeading
            title="Measure your success metrics"
            desc="Unlock insights with SpatialGrid Analytics. Track engagement, dwell time, & spatial interactions through real-time dashboards to understand user behavior and improve experience outcomes."
          />

          <div className="w-full flex justify-center my-7">
            <img
              alt=""
              src={imagePath?.successMactrix}
              className="w-[90%] max-w-[1100px] h-auto object-contain rounded-[10px]"
            />
          </div>
        </div>
      </div>

      {/* Signup Modal Component */}
      <SignupModal
        isOpen={showSignupModal}
        onClose={() => setShowSignupModal(false)}
      />
    </div>
  );
};

const DocumentationHeading = React.memo(({ title, desc }) => {
  return (
    <div className="w-full flex flex-col items-center gap-2">
      {title && (
        <div className="text-center font-jakarta font-bold text-xl sm:text-2xl md:text-2xl text-[#D8D8D8]">
          {title}
        </div>
      )}

      {desc && (
        <div className="text-[#757678] max-w-[830px] font-medium text-base sm:text-lg md:text-[20px] text-center px-4">
          {desc}
        </div>
      )}
    </div>
  );
});

export default PlatformOverview;
